import { MdCheck, MdClose } from "react-icons/md";
import Modal from "../Modal/Modal";
import { memo, ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ICON_SIZES } from "../../helpers/constants";
import clsx from "clsx";
import CheckBox from "../CheckBox/CheckBox";
import useMobile from "../../hooks/useMobile";
import { EButtonKind } from "../Button/ButtonTypes";

interface IConfirmDialog {
  title: string;
  onClose: () => void;
  onConfirm: () => void;
  description: string;
  confirmKind?: EButtonKind;
  isLoading?: boolean;
  icon?: ReactElement;
  cancelLabel?: string;
  showCancel?: boolean;
  note?: string;
  doubleConfirmRequired?: boolean;
}

const ConfirmDialog: React.FC<IConfirmDialog> = ({
  title,
  onClose,
  onConfirm,
  description,
  confirmKind = EButtonKind.SAVE,
  isLoading = false,
  cancelLabel = "",
  showCancel = true,
  icon = <></>,
  note = "",
  doubleConfirmRequired = false,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useMobile();

  const [doubleConfirm, setDoubleConfirm] = useState<boolean>(
    doubleConfirmRequired ? false : true
  );

  const actionIsDisabled = useMemo(() => {
    if (doubleConfirmRequired) {
      return !doubleConfirm || isLoading;
    }

    return isLoading;
  }, [isLoading, doubleConfirmRequired, doubleConfirm]);

  return (
    <Modal
      width="max-w-[700px] lg:min-w-[500px]"
      onEscape={() => {
        if (!isLoading && showCancel) {
          onClose();
        }
      }}
      className="!shadow-md !rounded-lg"
      closeOnOverlayClick={!isLoading && showCancel}
    >
      <Modal.Header
        onClose={() => {
          if (!isLoading && showCancel) {
            onClose();
          }
        }}
        isClosable={showCancel}
        title={title}
        icon={icon}
      />
      <Modal.Body>
        <div className={clsx(["flex", "flex-col", "w-full"])}>
          <p className={clsx(["text-sm", "md:text-base"])}>
            {description}
            {isMobile && (
              <>
                {note && (
                  <>
                    &nbsp;
                    <span className={clsx(["warning-text"])}>{note}</span>
                  </>
                )}
              </>
            )}
          </p>
          {!isMobile && (
            <>
              {note && (
                <p
                  className={clsx([
                    "text-xs",
                    "md:text-sm",
                    "warning-text",
                    {
                      "font-medium": doubleConfirmRequired,
                    },
                  ])}
                >
                  {note}
                </p>
              )}
            </>
          )}
          {doubleConfirmRequired && (
            <div
              className={clsx([
                "flex",
                "flex-row",
                "w-full",
                "my-3",
                "md:my-4",
              ])}
            >
              <CheckBox
                disabled={isLoading}
                checked={doubleConfirm}
                label="Estoy de acuerdo"
                onChange={(isChecked: boolean) => setDoubleConfirm(isChecked)}
              />
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            kind: confirmKind,
            onClick: onConfirm,
            text: t("shared.confirm"),
            icon: <MdCheck size={ICON_SIZES.MD} />,
            isLoading,
            disabled: actionIsDisabled,
            autoFocus: true,
          },
          {
            kind: EButtonKind.NORMAL,
            onClick: onClose,
            text: cancelLabel || t("shared.cancel"),
            icon: <MdClose size={ICON_SIZES.MD} />,
            disabled: isLoading,
            hide: !showCancel,
          },
        ]}
      />
    </Modal>
  );
};

export default memo(ConfirmDialog);
